"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ALL_BLOGS } from "@/lib/blog";
import { BookingModal } from "@/components/BookingModal";
import { ContactFooter } from "@/components/ContactFooter";
import { Calendar, Clock, ArrowRight } from "lucide-react";

export default function NewsAndArticlesPageClient() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <BookingModal open={showModal} onOpenChange={setShowModal} />

      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative pt-32 pb-16 px-4 text-center overflow-hidden">
          {/* Background image, lightly blurred, fading to white at the bottom */}
          <div className="absolute inset-0">
            <Image
              src="/News/News-hero.webp"
              alt="SkinSio Clinics News and Articles - Expert Skin and Aesthetic Treatment Guides"
              fill
              sizes="100vw"
              className="object-cover scale-110 blur-[2px]"
              priority
            />
            <div className="absolute inset-0 bg-background/35" />
            <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent via-background/70 to-background" />
          </div>

          {/* Foreground content */}
          <div className="relative z-10 max-w-3xl mx-auto">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">
              SkinSio Clinics · Vadodara
            </p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              News & Articles
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Expert advice, treatment guides and tips from our experienced
              Doctors & Plastic Surgeons in Vadodara.
            </p>
            <p className="mt-6 text-xs sm:text-sm md:text-lg font-bold text-foreground whitespace-nowrap">
              Doctor-Written Guides • Treatment Comparisons • Latest Advances in Aesthetic Medicine
            </p>
          </div>
        </section>

        {/* Article Grid */}
        <section className="max-w-5xl mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...ALL_BLOGS].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((blog) => (
              <Link
                key={blog.slug}
                href={`/news-and-articles/${blog.slug}`}
                className="group flex flex-col border rounded-2xl bg-card overflow-hidden hover:shadow-lg hover:border-primary/40 transition-all duration-200"
              >
                {/* Category badge */}
                <div className="px-5 pt-5">
                  <span className="inline-block text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                    {blog.category}
                  </span>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 p-5">
                  <h2 className="text-base font-bold mb-3 group-hover:text-primary transition-colors line-clamp-3">
                    {blog.title}
                  </h2>
                  <p className="text-sm text-muted-foreground line-clamp-3 flex-1">
                    {blog.excerpt}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {blog.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {blog.readTime}
                    </span>
                  </div>

                  <span className="inline-flex items-center gap-1 mt-3 text-sm text-primary font-medium group-hover:underline">
                    Read article <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {ALL_BLOGS.length === 0 && (
            <div className="text-center py-20 text-muted-foreground">
              <p className="text-lg">New articles coming soon!</p>
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="bg-primary/5 py-12 px-4 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold mb-3">Have a Skin or Hair Concern?</h2>
            <p className="text-muted-foreground mb-6">
              Book a consultation with our expert Doctors & Plastic Surgeons in Vadodara.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-block bg-primary text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Book Consultation
            </button>
          </div>
        </section>

        <ContactFooter />
      </main>
    </>
  );
}
